import Papa from 'papaparse';
import { normalizeStudyData, generateFilterOptions } from './dataProcessing';

class CSVDataLoader {
  constructor() {
    this.cache = new Map();
    this.isLoading = false;
  }

  async loadStudies(forceReload = false) {
    const cacheKey = 'eeg-studies';

    if (this.cache.has(cacheKey) && !forceReload) {
      return this.cache.get(cacheKey);
    }

    if (this.isLoading) {
      return this.loadingPromise;
    }

    this.isLoading = true;
    this.loadingPromise = this._fetchAndProcessCSV();

    try {
      const result = await this.loadingPromise;
      this.cache.set(cacheKey, result);
      return result;
    } finally {
      this.isLoading = false;
      this.loadingPromise = null;
    }
  }

  async _fetchAndProcessCSV() {
    const response = await fetch('/eeg-music-studies-database.csv');

    if (!response.ok) {
      throw new Error(`Failed to fetch CSV: ${response.status} ${response.statusText}`);
    }

    const csvText = await response.text();

    if (!csvText || csvText.trim().length === 0) {
      throw new Error('CSV file is empty or could not be read');
    }

    const firstLine = csvText.split('\n', 1)[0] || '';
    const tabCount = (firstLine.match(/\t/g) || []).length;
    const commaCount = (firstLine.match(/,/g) || []).length;
    const delimiter = tabCount > commaCount ? '\t' : ',';

    return new Promise((resolve, reject) => {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
        delimiter: delimiter,
        quoteChar: '"',
        escapeChar: '"',
        complete: (results) => {
          try {
            if (results.errors && results.errors.length > 0) {
              console.warn('CSV parse warnings:', results.errors);
            }

            if (!results.data || results.data.length === 0) {
              throw new Error('No data found in CSV file');
            }

            resolve(this._finalizeProcessing(results.data, results.meta.fields));
          } catch (error) {
            reject(error);
          }
        },
        error: (error) => {
          reject(new Error(`CSV parsing failed: ${error.message || error}`));
        }
      });
    });
  }

  _finalizeProcessing(rawData, columns) {
    if (!rawData || !Array.isArray(rawData)) {
      throw new Error('Invalid raw data provided to processing');
    }

    const studies = [];

    rawData.forEach((row, index) => {
      try {
        const processedStudy = normalizeStudyData(row, index);
        if (processedStudy) {
          studies.push(processedStudy);
        }
      } catch (error) {
        // Skip invalid rows silently
      }
    });

    if (studies.length === 0) {
      throw new Error('No valid studies could be processed from the CSV data');
    }

    const featureStats = {};
    studies.forEach(study => {
      if (study.normalizedFeatures && Array.isArray(study.normalizedFeatures)) {
        study.normalizedFeatures.forEach(feature => {
          featureStats[feature] = (featureStats[feature] || 0) + 1;
        });
      }
    });

    const sortedFeatures = Object.entries(featureStats)
      .sort((a, b) => b[1] - a[1])
      .map(([feature, count]) => ({
        name: feature,
        count: count,
        percentage: Math.round((count / studies.length) * 100)
      }));

    let filterOptions = {};
    try {
      filterOptions = generateFilterOptions(studies);
    } catch (error) {
      console.error('Error generating filter options:', error);
    }

    const years = studies.map(s => s.year).filter(y => y > 0);
    const yearRange = years.length > 0 ? {
      min: Math.min(...years),
      max: Math.max(...years)
    } : { min: 1975, max: 2025 };

    const metadata = {
      columns: columns || [],
      count: studies.length,
      yearRange,
      featureCategories: sortedFeatures,
      filterOptions
    };

    return {
      studies,
      metadata,
      timestamp: Date.now()
    };
  }

  clearCache() {
    this.cache.clear();
  }
}

export const csvLoader = new CSVDataLoader();
